import type { HTMLAttributes } from "react";
import { cn } from "../lib/cn";

const detectMac = () =>
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

const MAC_LABELS: Record<string, string> = {
  mod: "⌘",
  cmd: "⌘",
  ctrl: "⌃",
  alt: "⌥",
  option: "⌥",
  shift: "⇧",
  enter: "↵",
  esc: "Esc",
};
const PC_LABELS: Record<string, string> = {
  mod: "Ctrl",
  cmd: "Ctrl",
  ctrl: "Ctrl",
  alt: "Alt",
  option: "Alt",
  shift: "Shift",
  enter: "Enter",
  esc: "Esc",
};

/** Turns "mod+shift+h" into platform labels: Ctrl Shift H on Windows, symbols on macOS. */
export function shortcutParts(keys: string, mac = detectMac()): string[] {
  const table = mac ? MAC_LABELS : PC_LABELS;
  return keys
    .split("+")
    .map((k) => k.trim())
    .filter(Boolean)
    .map((k) => table[k.toLowerCase()] ?? (k.length === 1 ? k.toUpperCase() : k));
}

export interface KbdProps extends HTMLAttributes<HTMLElement> {
  /** Shortcut string such as "M", "mod+k" or "ctrl+shift+h". Rendered per platform. */
  keys: string;
  size?: "sm" | "md";
  /** Force the macOS or Windows rendering. */
  platform?: "mac" | "pc";
  /** On a colored button (primary, answer): a light key on the fill. */
  onColor?: boolean;
}

/** Keyboard shortcut hint. Never the only way to discover an action. */
export function Kbd({
  keys,
  size = "md",
  platform,
  onColor = false,
  className,
  ...props
}: KbdProps) {
  const parts = shortcutParts(keys, platform ? platform === "mac" : detectMac());
  return (
    <span className={cn("inline-flex items-center gap-0.5", className)} {...props}>
      {parts.map((p, i) => (
        <kbd
          // biome-ignore lint/suspicious/noArrayIndexKey: shortcut parts are positional and static
          key={i}
          className={cn(
            "inline-flex items-center justify-center rounded-xs font-medium font-sans",
            onColor
              ? "bg-white/20 text-white"
              : "bg-fill-2 text-ink-2 shadow-[inset_0_0_0_1px_var(--vd-hairline),inset_0_-1px_0_0_var(--vd-hairline)]",
            size === "sm"
              ? "h-4 min-w-4 px-1 text-[10px] leading-none"
              : "h-5 min-w-5 px-1.5 text-micro",
          )}
        >
          {p}
        </kbd>
      ))}
    </span>
  );
}
